/**
 * An email address as a `mailto:` link, or as plain text when it is not one plain address.
 *
 * Stored email cells are free text (staff-typed, or taken from an inbound message). Interpolating
 * one into `mailto:${...}` lets `a@b.com?bcc=x@evil.com` open the mail app with a hidden recipient
 * already filled in (#6443). Every email link in the client goes through here instead, so the one
 * rule in `lib/contactHref` decides, and `scripts/guards/check-no-raw-mailto.mjs` fails a new raw one.
 *
 * Refused values are still readable, in a neutral colour with no link styling. Pass
 * `whenRefused="hide"` for an icon-only link or an action row, where a dead control would only mislead.
 *
 * @module components/shared/EmailLink
 */
import type { HTMLAttributes, ReactNode } from 'react';
import { safeContactHref } from '../../lib/contactHref';

interface EmailLinkProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  email: string | null | undefined;
  /** What to show; defaults to the address itself. */
  children?: ReactNode;
  /** `text` (default): show the content without a link. `hide`: render nothing. */
  whenRefused?: 'text' | 'hide';
  /** Link-only: dropped, with the link itself, when the address is refused. */
  target?: string;
  rel?: string;
}

/** Link colour, underline and hover tokens: a refused address must not still look clickable. */
const LINK_LOOK = /^(hover:|focus:|group-hover:|underline$|cursor-pointer$|transition|text-(sky|blue|indigo|cyan)-\d|text-\[#)/;

/** The caller's layout and size classes, with the link look removed and a neutral colour added. */
function plainTextClassName(className: string | undefined): string {
  const kept = (className ?? '').split(/\s+/).filter((token) => token && !LINK_LOOK.test(token));
  return [...kept, 'text-slate-600'].join(' ');
}

export function EmailLink({
  email,
  children,
  whenRefused = 'text',
  target,
  rel,
  className,
  ...passedProps
}: EmailLinkProps) {
  // `href` is not part of this component's props, but an untyped caller can still pass one. It is
  // dropped here, and the validated value is set AFTER the spread below, so nothing passed in can
  // override the one invariant this component exists for.
  const { href: _strayHref, ...elementProps } = passedProps as typeof passedProps & { href?: unknown };
  const address = (email ?? '').trim();
  if (!address) return null;

  const href = safeContactHref('mailto', address);
  const content = children ?? address;

  if (!href) {
    if (whenRefused === 'hide') return null;
    // Everything but the link itself carries over (aria-label, id, data-*, onClick, drag guards),
    // so a click or a drag on the refused text behaves exactly as it did on the link: a card that
    // stops the press from starting a drag, or the click from opening the row, still does.
    return (
      <span {...elementProps} className={plainTextClassName(className)}>
        {content}
      </span>
    );
  }
  return (
    <a {...elementProps} href={href} target={target} rel={rel} className={className}>
      {content}
    </a>
  );
}
